import { Component, OnInit, ChangeDetectionStrategy, signal, computed, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';
import { GlobalPresenter } from '../../../services/global/presenter/global.presenter';
import { CustomerActiveStatus, CustomerTypesEnum } from '../../../constants/customers.enum';
import { UserRolesEnum } from '../../../constants/user-roles.enum';
import { ResumeDataService } from './services/resume-data.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { QuickbooksService } from '../../../services/quickbooks/quickbooks.service';
import { ClienteInformationCardComponent } from '../../../shared/components/cliente-information-card/cliente-information-card.component';
import { OrderInfoModalComponent } from '../../../shared/widgets/orders/order-info-modal/order-info-modal.component';


@Component({
    selector: 'app-resume',
    templateUrl: './resume.component.html',
    styleUrls: ['./resume.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ResumeDataService],
    standalone: false
})
export class ResumeComponent implements OnInit {
  @ViewChild('headerCard') headerCard!: ClienteInformationCardComponent;
  // Injected services
  private readonly modalService = inject(NgbModal);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(ToastrService);
  private readonly globalPresenter = inject(GlobalPresenter);
  private readonly qbService = inject(QuickbooksService);
  readonly resumeData = inject(ResumeDataService);

  // ── Module context (set by route data: 'eco' | 'uco') ─────────────────
  // ResumeComponent is shared between UCO and ECO feature modules.
  // `data.module` is reserved by ModulePermissionGuard (must stay as-is).
  // `data.contextModule` is a dedicated key for navigation context and
  // defaults to 'uco' for backward-compatibility.
  readonly module = (this.route.snapshot.data['contextModule'] ?? 'uco') as 'eco' | 'uco';
  readonly backUrl = computed(() =>
    this.module === 'eco' ? '/eco/clients' : '/uco/business/megamenu2'
  );

  //Constants
  readonly CustomerTypesEnum = CustomerTypesEnum;
  readonly base_url = environment.base_url;

  //User / permissions state
  readonly isEnabled = signal(false);
  readonly qbLinked = signal(false);

  //Derived client state
  readonly isClient = computed(() => this.resumeData.clientInfomationTabData()?.status === 'Client');
  readonly hasProject = computed(() => !!this.resumeData.clientInfomationTabData()?.project);
  readonly is_customer_inactive = computed(() => {
    const raw = this.resumeData.clientInfomationTabData();
    return raw ? raw.status !== CustomerActiveStatus.Active : false;
  });


  ngOnInit(): void {
    // Propagate navigation context to the shared service so all child tabs
    // can resolve module-relative URLs without re-reading route data.
    this.resumeData.contextModule.set(this.module);

    // Setup user data
    const userData = this.globalPresenter.getUser();
    this.isEnabled.set(userData?.role === UserRolesEnum.ADMIN_ROLE || userData?.role === UserRolesEnum.SUPER_ADMIN_ROLE);
    const customerId = this.route.snapshot.paramMap.get('id') ?? '';
    this.resumeData.clientID.set(customerId);

    // Fetch oil collection specifically to support the client tenure header info
    this.resumeData.fetchOilCollectionData().catch(() => {});

    // Fetch QB link status for tab icon
    if (customerId) {
      this.qbService.getLinkStatus(customerId).subscribe({
        next: (status) => this.qbLinked.set(status.linked)
      });
    }

    // Listen for QB sync requests from child widgets (e.g. reconciliation payment via QB Balance)
    this.resumeData.qbSyncRequested$.subscribe(() => {
      this.headerCard?.refreshQbData();
    });
  }

  // 计算 clientTenure
  readonly clientTenure = computed(() => this.resumeData.clientTenure());

  readonly activeTabIndex = signal(0);
  readonly activeTabKey = signal<string>('basic');
  readonly visitedTabs = signal(new Set<number | string>([0, 'basic']));

  //Tab Management
  onTabChanged(index: number): void {
    this.activeTabIndex.set(index);
    const visited = new Set(this.visitedTabs());
    visited.add(index);
    this.visitedTabs.set(visited);
  }

  onTabChange(event: { index: number; key: string }): void {
    this.activeTabIndex.set(event.index);
    if (event.key) {
      this.activeTabKey.set(event.key);
    }
    const visited = new Set(this.visitedTabs());
    visited.add(event.index);
    if (event.key) {
      visited.add(event.key);
    }
    this.visitedTabs.set(visited);
  }

  /** Called when QB tab syncs data — refreshes header card balance */
  onQbDataChanged(): void {
    this.headerCard?.refreshQbData();
  }


  /** Abre OrderInfoModalComponent para la orden seleccionada.
   *  Recibe el _id (string) emitido por CustomerOrdersTabComponent.
   *  Sigue el mismo patrón que management-order.component.ts:openInfoModal. */
  openOrderDetailModal(orderId: string): void {
    const modalRef = this.modalService.open(OrderInfoModalComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });

    const ref = modalRef as any;
    const componentRef = ref.componentRef || ref._contentRef?.componentRef;

    if (componentRef) {
      componentRef.setInput('ident_order', orderId);
    } else {
      modalRef.componentInstance.ident_order = orderId;
    }

    modalRef.result.then((result: any) => {
      if (result?.action === 'refresh') {
        // Refrescar los datos de órdenes del tab
        const customerId = this.route.snapshot.paramMap.get('id') ?? '';
        if (customerId) {
          // El tab se auto-refresca cuando se invalide el signal clientOrdersTab
          this.resumeData.clientOrdersTab.set(null);
        }
      }
    }).catch(() => {});
  }

  /** Navigate back to the listing page of the module the Resume was opened from. */
  goBack(): void {
    this.router.navigateByUrl(this.backUrl());
  }
}
